// ComercialContatos — listagem global de contatos com modelo many-to-many.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Select, Button, Tag, Modal, Typography, Space, Form, Row, Col,
  Checkbox, Card, App as AntApp, Flex,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, StarFilled, CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Empresa {
  cliente_id: number;
  cliente_nome: string;
  papel: string | null;
  principal: boolean;
  cargo_neste: string | null;
  observacoes: string | null;
}

interface Contato {
  id: number;
  cliente_id: number | null;
  cliente_nome: string | null;
  empresas: Empresa[];
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  linkedin: string | null;
  decisor: boolean;
  observacoes: string | null;
}

interface Cliente {
  id: number;
  razao_social: string;
}

function ComercialContatosInner() {
  const { message, modal } = AntApp.useApp();
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState<Contato | null>(null);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      const [ctRes, clRes] = await Promise.all([
        axios.get(`${API_URL}/comercial/contatos`, { headers }),
        axios.get(`${API_URL}/comercial/clientes`, { headers }),
      ]);
      setContatos(ctRes.data.body ? JSON.parse(ctRes.data.body) : ctRes.data);
      setClientes(clRes.data.body ? JSON.parse(clRes.data.body) : clRes.data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  function excluir(id: number) {
    modal.confirm({
      title: 'Desativar este contato?',
      content: 'Ele será removido de TODAS empresas associadas.',
      okText: 'Desativar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/comercial/contatos/${id}`, { headers: { Authorization: token } });
          message.success('Contato desativado.');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + e.message);
        }
      },
    });
  }

  const filtrados = contatos.filter(c => {
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (
      c.nome?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.cargo?.toLowerCase().includes(q) ||
      (c.empresas || []).some(e => e.cliente_nome?.toLowerCase().includes(q))
    );
  });

  const columns: ColumnsType<Contato> = [
    {
      title: 'Nome', dataIndex: 'nome', key: 'n',
      render: (v, r) => (
        <Space size={4}>
          <Text strong style={{ color: '#020D7F' }}>{v}</Text>
          {r.decisor && <Tag color="success" style={{ margin: 0 }}>DECISOR</Tag>}
        </Space>
      ),
    },
    {
      title: 'Empresas', key: 'e',
      render: (_, c) => <EmpresasBadges empresas={c.empresas || []} />,
    },
    { title: 'Cargo', dataIndex: 'cargo', key: 'c', render: (v) => v || '—' },
    {
      title: 'E-mail', dataIndex: 'email', key: 'm',
      render: (v) => v ? <a href={`mailto:${v}`} style={{ color: '#0930D0' }}>{v}</a> : '—',
    },
    { title: 'Telefone', dataIndex: 'telefone', key: 't', render: (v) => v || '—' },
    {
      title: 'Ações', key: 'a', width: 90, align: 'center',
      render: (_, c) => (
        <Space size={4}>
          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => { setEditando(c); setShowForm(true); }} title="Editar" />
          <Button size="small" type="text" danger icon={<DeleteOutlined />}
            onClick={() => excluir(c.id)} title="Desativar" />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <Flex gap={12} align="center" style={{ marginBottom: 16 }} wrap="wrap">
        <Input
          placeholder="Buscar por nome, e-mail, cargo ou empresa..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {contatos.length} contato(s) · {contatos.filter(c => (c.empresas?.length || 0) > 1).length} em múltiplas empresas
        </Text>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => { setEditando(null); setShowForm(true); }}
          style={{ marginLeft: 'auto' }}>
          Novo Contato
        </Button>
      </Flex>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtrados}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: contatos.length === 0 ? 'Nenhum contato cadastrado.' : 'Nenhum resultado.' }}
        scroll={{ x: 'max-content' }}
      />

      {showForm && (
        <FormContato
          contato={editando}
          clientes={clientes}
          onClose={() => { setShowForm(false); setEditando(null); }}
          onSaved={async () => { await carregar(); setShowForm(false); setEditando(null); }}
        />
      )}
    </div>
  );
}

function EmpresasBadges({ empresas }: { empresas: Empresa[] }) {
  if (!empresas || empresas.length === 0) return <Text type="secondary">—</Text>;
  if (empresas.length === 1) {
    const e = empresas[0];
    return (
      <span title={`${e.cargo_neste || ''} ${e.papel ? '· ' + e.papel : ''}`} style={{ color: '#555' }}>
        {e.cliente_nome}
      </span>
    );
  }
  return (
    <Space size={4} wrap>
      {empresas.map(e => (
        <Tag
          key={e.cliente_id}
          title={`${e.cargo_neste || ''} ${e.papel ? '· ' + e.papel : ''}`}
          color={e.principal ? 'processing' : 'default'}
          style={{ margin: 0 }}
        >
          {e.principal && <StarFilled style={{ color: '#B07500', fontSize: 9, marginRight: 4 }} />}
          {e.cliente_nome}
        </Tag>
      ))}
    </Space>
  );
}

function FormContato({ contato, clientes, onClose, onSaved }: {
  contato: Contato | null;
  clientes: Cliente[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { message, modal } = AntApp.useApp();
  const [form, setForm] = useState({
    nome: contato?.nome || '',
    cargo: contato?.cargo || '',
    email: contato?.email || '',
    telefone: contato?.telefone || '',
    linkedin: contato?.linkedin || '',
    decisor: contato?.decisor || false,
    observacoes: contato?.observacoes || '',
  });
  const [empresas, setEmpresas] = useState<Empresa[]>(contato?.empresas || []);
  const [novaEmpresaId, setNovaEmpresaId] = useState<number | undefined>(undefined);
  const [novoCargoNeste, setNovoCargoNeste] = useState('');
  const [novoPrincipal, setNovoPrincipal] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const set = (k: string, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  function adicionarEmpresa() {
    if (!novaEmpresaId) { message.warning('Selecione uma empresa'); return; }
    if (empresas.find(e => e.cliente_id === novaEmpresaId)) {
      message.warning('Esta empresa já está associada');
      return;
    }
    const cli = clientes.find(c => c.id === novaEmpresaId);
    if (!cli) return;
    const nova: Empresa = {
      cliente_id: novaEmpresaId,
      cliente_nome: cli.razao_social,
      papel: null,
      principal: novoPrincipal,
      cargo_neste: novoCargoNeste || null,
      observacoes: null,
    };
    if (novoPrincipal) {
      setEmpresas([...empresas.map(e => ({ ...e, principal: false })), nova]);
    } else {
      setEmpresas([...empresas, nova]);
    }
    setNovaEmpresaId(undefined);
    setNovoCargoNeste('');
    setNovoPrincipal(false);
  }

  function removerEmpresa(cliId: number) {
    setEmpresas(empresas.filter(e => e.cliente_id !== cliId));
  }

  function tornarPrincipal(cliId: number) {
    setEmpresas(empresas.map(e => ({ ...e, principal: e.cliente_id === cliId })));
  }

  async function handleSave() {
    if (!form.nome.trim()) { message.warning('Nome é obrigatório'); return; }
    if (empresas.length === 0) {
      const ok = await new Promise<boolean>(resolve =>
        modal.confirm({
          title: 'Nenhuma empresa associada',
          content: 'Continuar mesmo assim? O contato fica órfão até você associar a uma empresa.',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        })
      );
      if (!ok) return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const headers = { Authorization: token };
      let contatoId = contato?.id;

      if (contato) {
        await axios.put(`${API_URL}/comercial/contatos/${contato.id}`, form, { headers });
      } else {
        const r = await axios.post(`${API_URL}/comercial/contatos`,
          { ...form, action: 'criar' }, { headers });
        const data = r.data.body ? JSON.parse(r.data.body) : r.data;
        contatoId = data.id;
      }

      const empresasAntes = (contato?.empresas || []);
      const idsAntes = new Set(empresasAntes.map(e => e.cliente_id));
      const idsAgora = new Set(empresas.map(e => e.cliente_id));

      for (const e of empresas) {
        if (!idsAntes.has(e.cliente_id) ||
            empresasAntes.find(x => x.cliente_id === e.cliente_id)?.principal !== e.principal ||
            empresasAntes.find(x => x.cliente_id === e.cliente_id)?.cargo_neste !== e.cargo_neste) {
          await axios.post(`${API_URL}/comercial/contatos`, {
            action: 'associar',
            cliente_id: e.cliente_id,
            contato_id: contatoId,
            principal: e.principal,
            cargo_neste: e.cargo_neste,
          }, { headers });
        }
      }
      for (const e of empresasAntes) {
        if (!idsAgora.has(e.cliente_id)) {
          await axios.post(`${API_URL}/comercial/contatos`, {
            action: 'desassociar',
            cliente_id: e.cliente_id,
            contato_id: contatoId,
          }, { headers });
        }
      }

      message.success(contato ? 'Contato atualizado.' : 'Contato criado.');
      await onSaved();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally {
      setSalvando(false);
    }
  }

  const empresasDisponiveis = clientes.filter(c => !empresas.find(e => e.cliente_id === c.id));

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={handleSave}
      okText={contato ? 'Salvar' : 'Criar Contato'}
      cancelText="Cancelar"
      confirmLoading={salvando}
      title={contato ? 'Editar Contato' : 'Novo Contato'}
      width={680}
      destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Nome" required>
              <Input value={form.nome} onChange={e => set('nome', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Cargo (geral)">
              <Input value={form.cargo} onChange={e => set('cargo', e.target.value)} placeholder="Ex.: Engenheiro Civil" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="E-mail">
              <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Telefone">
              <Input value={form.telefone} onChange={e => set('telefone', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="LinkedIn">
              <Input value={form.linkedin} onChange={e => set('linkedin', e.target.value)} placeholder="https://linkedin.com/in/..." />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item>
              <Checkbox checked={form.decisor} onChange={e => set('decisor', e.target.checked)}>
                Decisor (pessoa-chave)
              </Checkbox>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Observações">
              <Input.TextArea value={form.observacoes} onChange={e => set('observacoes', e.target.value)} rows={2} />
            </Form.Item>
          </Col>
        </Row>

        <Card
          size="small"
          title={
            <Text style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>
              Empresas associadas ({empresas.length}) · ★ marca a empresa principal
            </Text>
          }
          style={{ background: '#FAFBFF', marginBottom: 12 }}
        >
          {empresas.length === 0 ? (
            <Text type="secondary" italic style={{ fontSize: 11 }}>
              Nenhuma empresa associada ainda.
            </Text>
          ) : (
            <Space direction="vertical" size={6} style={{ width: '100%', marginBottom: 12 }}>
              {empresas.map(e => (
                <Flex
                  key={e.cliente_id}
                  align="center"
                  gap={8}
                  style={{
                    padding: '6px 10px',
                    background: '#fff',
                    borderRadius: 5,
                    border: e.principal ? '1px solid #0930D0' : '1px solid #ddd',
                  }}
                >
                  <span style={{ flex: 1, fontSize: 12, color: '#020D7F', fontWeight: e.principal ? 600 : 400 }}>
                    {e.principal && <StarFilled style={{ color: '#B07500', marginRight: 4 }} />}
                    {e.cliente_nome}
                    {e.cargo_neste && (
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>· {e.cargo_neste}</Text>
                    )}
                  </span>
                  {!e.principal && (
                    <Button size="small" onClick={() => tornarPrincipal(e.cliente_id)} title="Marcar como principal">
                      Tornar principal
                    </Button>
                  )}
                  <Button size="small" type="text" danger icon={<CloseOutlined />}
                    onClick={() => removerEmpresa(e.cliente_id)} title="Desassociar" />
                </Flex>
              ))}
            </Space>
          )}

          <Row gutter={6} align="middle">
            <Col flex="auto">
              <Select
                placeholder="+ Adicionar empresa..."
                value={novaEmpresaId}
                onChange={setNovaEmpresaId}
                options={empresasDisponiveis.map(c => ({ value: c.id, label: c.razao_social }))}
                showSearch
                style={{ width: '100%' }}
              />
            </Col>
            <Col flex="180px">
              <Input value={novoCargoNeste} onChange={e => setNovoCargoNeste(e.target.value)}
                placeholder="Cargo aqui (opcional)" />
            </Col>
            <Col>
              <Checkbox checked={novoPrincipal} onChange={e => setNovoPrincipal(e.target.checked)}>
                <Text style={{ fontSize: 11 }}>Principal</Text>
              </Checkbox>
            </Col>
            <Col>
              <Button type="primary" icon={<PlusOutlined />} disabled={!novaEmpresaId} onClick={adicionarEmpresa}>
                Add
              </Button>
            </Col>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
}

export default function ComercialContatos() {
  return (
    <AntApp>
      <ComercialContatosInner />
    </AntApp>
  );
}
